import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, Save, X, Calendar, Smile, Activity, Moon, Utensils, Pill, Tag, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';

const JournalChat = ({ onComplete }) => {
  const [entries, setEntries] = useState([]);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const messagesEndRef = useRef(null);

  const templates = [
    { id: 'free_form', icon: Sparkles, label: 'Free Form', description: 'Tell me about your day' },
    { id: 'daily_checkin', icon: Smile, label: 'Daily Check-in', description: 'Mood, energy & wellbeing' },
    { id: 'symptom_tracker', icon: Activity, label: 'Symptom Tracker', description: 'Track health symptoms' },
    { id: 'medication_log', icon: Pill, label: 'Medication Log', description: 'Log medications & effects' },
    { id: 'gratitude', icon: TrendingUp, label: 'Gratitude Journal', description: 'Practice gratitude' },
    { id: 'food_diary', icon: Utensils, label: 'Food Diary', description: 'Track meals & nutrition' }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentEntry?.conversation]);

  const startNewEntry = async (template = 'free_form', initialMessage = null) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('http://localhost:5000/api/journal/entries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ template, initialMessage })
      });

      const data = await response.json();
      
      if (response.ok) {
        setCurrentEntry(data.entry);
        setShowTemplates(false);
        toast.success('Journal started! Tell me about your day');
      } else {
        toast.error(data.message || 'Failed to start journal');
      }
    } catch (error) {
      console.error('Error starting entry:', error);
      toast.error('Failed to start journal entry');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !currentEntry || isLoading) return;

    try {
      setIsLoading(true);
      const userMessage = message;
      setMessage('');

      const token = localStorage.getItem('token');
      const response = await fetch(
        `http://localhost:5000/api/journal/entries/${currentEntry._id}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ message: userMessage })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setCurrentEntry({
          ...currentEntry,
          conversation: data.conversation
        });

        if (data.shouldComplete) {
          toast('You can save this entry now!', {
            icon: '💾',
            duration: 3000
          });
        }
      } else {
        toast.error(data.message || 'Failed to send message');
        setMessage(userMessage); // Restore message
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const completeEntry = async () => {
    if (!currentEntry || !currentEntry._id) {
      toast.error('No active journal entry to save');
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please log in to save entries');
        window.location.href = '/login';
        return;
      }
      
      console.log('🔄 Saving journal entry:', currentEntry._id);
      
      const response = await fetch(
        `http://localhost:5000/api/journal/entries/${currentEntry._id}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      console.log('✅ Journal save response:', data);
      
      if (response.ok && data.success) {
        toast.success('✅ Journal entry saved successfully!');
        setCurrentEntry(null);
        await fetchEntries(); // Refresh list
        if (onComplete) {
          onComplete(); // Call parent callback
        }
      } else {
        console.error('❌ Save failed:', data);
        toast.error(data.message || 'Failed to save entry. Please try again.');
      }
    } catch (error) {
      console.error('❌ Network error saving entry:', error);
      toast.error('Network error: ' + error.message + '. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEntry = () => {
    if (window.confirm('Discard this journal entry?')) {
      setCurrentEntry(null);
      toast('Entry discarded', { icon: '🗑️' });
    }
  };

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found, skipping entry fetch');
        return;
      }
      
      console.log('📖 Fetching journal entries...');
      const response = await fetch('http://localhost:5000/api/journal/entries?status=completed&limit=10', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      console.log('📖 Fetched entries response:', data);
      
      if (response.ok && data.success) {
        setEntries(data.entries || []);
        console.log('✅ Loaded', data.entries?.length || 0, 'entries');
      } else {
        console.error('Failed to fetch entries:', data);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const getMoodEmoji = (mood) => {
    const emojis = {
      very_happy: '😄',
      happy: '😊',
      neutral: '😐',
      sad: '😢',
      very_sad: '😭',
      anxious: '😰',
      stressed: '😫',
      energetic: '⚡',
      tired: '😴',
      calm: '😌'
    };
    return emojis[mood] || '✨';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                <Sparkles className="text-purple-500" />
                AI Health Journal
              </h1>
              <p className="text-gray-600 mt-1">Reflect, track, and grow with AI guidance</p>
            </div>
            
            {!currentEntry && (
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Start New Entry
              </button>
            )}
          </div>
        </div>

        {/* Template Selection */}
        {showTemplates && !currentEntry && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Choose a template</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => startNewEntry(template.id)}
                  className="flex items-start gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                >
                  <template.icon className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-800">{template.label}</h3>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat Interface */}
        {currentEntry && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span className="font-semibold">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={completeEntry}
                  disabled={isLoading || currentEntry.conversation.length < 4}
                  className="px-4 py-2 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Entry
                </button>
                <button
                  onClick={cancelEntry}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="h-96 overflow-y-auto p-6 space-y-4 bg-gray-50">
              {currentEntry.conversation.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-md px-4 py-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-gray-800 shadow-sm'
                    }`}
                  >
                    {msg.role === 'ai' && (
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span className="text-xs font-semibold text-purple-600">AI Assistant</span>
                      </div>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white px-4 py-3 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce delay-100" />
                      <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce delay-200" />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type your message..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 disabled:opacity-50"
                />
                <button
                  onClick={sendMessage}
                  disabled={!message.trim() || isLoading}
                  className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recent Entries */}
        {!currentEntry && !showTemplates && entries.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Entries</h2>
            <div className="space-y-3">
              {entries.map((entry) => (
                <div
                  key={entry._id}
                  className="p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all cursor-pointer"
                  onClick={() => window.location.href = `/journal/history`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{getMoodEmoji(entry.mood)}</span>
                        <span className="text-sm text-gray-600">
                          {new Date(entry.entryDate).toLocaleDateString()}
                        </span>
                        {entry.moodScore && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                            Mood: {entry.moodScore}/10
                          </span>
                        )}
                      </div>
                      <p className="text-gray-800 text-sm">{entry.summary}</p>
                      {entry.tags && entry.tags.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {entry.tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => window.location.href = '/journal/history'}
              className="w-full mt-4 px-4 py-3 border-2 border-purple-600 text-purple-600 rounded-xl font-semibold hover:bg-purple-50 transition-all"
            >
              View All Entries & Insights
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default JournalChat;
